import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import WahaSessionsAPI from 'dashboard/api/wahaSessions';

// The WAHA chatwoot app pushes session.status (waha_session.status over the
// cable), so polling is only a fallback: often while the session settles or
// waits for the QR scan (which WAHA rotates), rarely once it is stable, never
// while the tab is hidden, and backing off while WAHA does not answer.
const TRANSIENT_STATUSES = ['pending', 'starting', 'scan_qr', 'connecting'];
const TRANSIENT_POLL_MS = 5_000;
const QR_POLL_MS = 15_000;
const STABLE_POLL_MS = 60_000;
const MAX_BACKOFF_MS = 60_000;

export function useWahaSession(sessionId) {
  const { t } = useI18n();

  const session = ref(null);
  const qrCode = ref('');
  const isLoading = ref(true);
  const isRefreshing = ref(false);
  const pendingAction = ref('');

  let timer = null;
  let failures = 0;

  const status = computed(() => session.value?.status || 'pending');
  const isConnected = computed(() => status.value === 'connected');
  const isScanningQr = computed(() => status.value === 'scan_qr');

  const merge = patch => {
    session.value = { ...(session.value || {}), ...patch };
  };

  const clearTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const nextDelay = () => {
    if (failures > 0) {
      return Math.min(TRANSIENT_POLL_MS * 2 ** failures, MAX_BACKOFF_MS);
    }
    if (isScanningQr.value) return QR_POLL_MS;
    return TRANSIENT_STATUSES.includes(status.value)
      ? TRANSIENT_POLL_MS
      : STABLE_POLL_MS;
  };

  const refreshQr = async () => {
    const { data } = await WahaSessionsAPI.getQRCode(sessionId.value, true);
    qrCode.value = data.qr_code || '';
    merge({ status: data.status, qr_code: data.qr_code });
  };

  const refreshStatus = async () => {
    const { data } = await WahaSessionsAPI.getStatus(sessionId.value);
    merge({
      status: data.status,
      phone_number: data.phone_number,
      connected: data.connected,
      engine: data.waha_data?.engine?.engine || session.value?.engine,
    });
  };

  const schedule = () => {
    clearTimer();
    if (!sessionId.value || document.hidden) return;
    // eslint-disable-next-line no-use-before-define
    timer = setTimeout(poll, nextDelay());
  };

  async function poll() {
    try {
      if (isScanningQr.value) {
        await refreshQr();
      } else {
        await refreshStatus();
      }
      failures = 0;
    } catch (_) {
      failures += 1;
    }
    schedule();
  }

  const refresh = async () => {
    if (!sessionId.value || isRefreshing.value) return;
    isRefreshing.value = true;
    try {
      await refreshStatus();
      if (isScanningQr.value) await refreshQr();
      failures = 0;
    } catch (error) {
      useAlert(parseAPIErrorResponse(error));
    } finally {
      isRefreshing.value = false;
      schedule();
    }
  };

  const load = async () => {
    if (!sessionId.value) {
      isLoading.value = false;
      return;
    }
    try {
      const { data } = await WahaSessionsAPI.show(sessionId.value);
      session.value = data;
      qrCode.value = data.qr_code || '';
    } catch (error) {
      useAlert(parseAPIErrorResponse(error));
    } finally {
      isLoading.value = false;
      schedule();
    }
  };

  const ACTIONS = {
    start: () => WahaSessionsAPI.start(sessionId.value),
    stop: () => WahaSessionsAPI.stop(sessionId.value),
    restart: () => WahaSessionsAPI.restart(sessionId.value),
    logout: () => WahaSessionsAPI.logout(sessionId.value),
    recreate_app: () => WahaSessionsAPI.recreateApp(sessionId.value),
  };

  const runAction = async name => {
    if (pendingAction.value) return;
    pendingAction.value = name;
    const key = name.toUpperCase();
    try {
      const { data } = await ACTIONS[name]();
      if (data?.status) merge({ status: data.status });
      useAlert(t(`INBOX_MGMT.WAHA.${key}_SUCCESS`));
    } catch (error) {
      useAlert(
        parseAPIErrorResponse(error) || t(`INBOX_MGMT.WAHA.${key}_ERROR`)
      );
    } finally {
      pendingAction.value = '';
      failures = 0;
      schedule();
    }
  };

  useEmitter(BUS_EVENTS.WAHA_SESSION_STATUS, data => {
    if (!sessionId.value || data?.id !== sessionId.value) return;
    merge({
      status: data.status,
      phone_number: data.phone_number,
      connected: data.connected,
    });
    if (data.status === 'scan_qr') {
      refreshQr().catch(() => {});
    }
    failures = 0;
    schedule();
  });

  // Events missed while the socket was down
  useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, () => {
    if (sessionId.value) poll();
  });

  const onVisibilityChange = () => {
    if (document.hidden) {
      clearTimer();
    } else if (sessionId.value) {
      poll();
    }
  };

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    clearTimer();
  });

  watch(sessionId, load, { immediate: true });

  return {
    session,
    qrCode,
    status,
    isConnected,
    isScanningQr,
    isLoading,
    isRefreshing,
    pendingAction,
    load,
    refresh,
    runAction,
    applySession: data => {
      session.value = data;
    },
  };
}
